const templateCache = {};
/**
 * return template of the HTML fragment
 * @param {Array} - split static string parts of a template
 * @param {Array} - dynamic values of the template
 * @retun {String}
 */
// TODO: I want to add cache for avoid recreating templates

const globalStylesheet = new CSSStyleSheet();
globalStylesheet.replaceSync(`
    ::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    ::-webkit-scrollbar-thumb {
      background: var(--fc-color-gray-300);
      border: 4px solid transparent;
      -webkit-background-clip: padding-box; 
      background-clip: padding-box;
      box-sizing: border-box;
      border-radius: 10px;
    }
    ::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 16px;
    }
    
    body {
      scrollbar-width: thin;
      scrollbar-color: var(--fc-color-gray-300) transparent; /* Thumb color, Track color */
    }
`);

export const html = (strings, ...args) => {
  const key = strings.join('') + args.join('');

  if (!(key in templateCache)) {
    templateCache[key] = strings.reduce(
      (acc, currentString, index) => acc + currentString + (args[index] || ''),
      ''
    );
  }
  return templateCache[key];
};

/**
 * Base class for other renderer mixins
 *
 * Attaches shadow root to current element, adds some helper properties
 * and adds method to initialise the shadow root (implemented by sub classes)
 * or use light dom in case of IE also provide ability to choose renderer
 *
 * @mixin RendererMixin
 * @param {*} clazz  The class to mix in to
 * @return {*}            The mixed class
 */
export const RendererMixin = clazz =>
  /** @extends { HTMLElement } */
  class extends clazz {
    /**
     * indicate whether a component or window is in ie mode
     * @return {Boolean}
     */
    get isCSS() {
      return window.isCSS || this.hasAttribute('css');
    }

    /**
     * return the root element usually shadowRoot or this depends on IE
     * @retun {Boolean}
     */
    get root() {
      if (!this.isCSS) {
        return this.shadowRoot;
      }
      return this;
    }

    /** inherit docs */
    constructor() {
      super();

      if (!this.isCSS) {
        this.attachShadow({ mode: 'open' });
      }

      this._query = this.root.querySelector.bind(this.root);
      this._queryAll = this.root.querySelectorAll.bind(this.root);
    }

    /**
     * return the template string to be rendered
     * @return {String} rendered template
     */
    render() {
      return '';
    }

    /**
     * render with a renderer function of just parsing string to HTML
     */
    requestUpdate() {
      const lightDom = this.innerHTML;
      if (!this.constructor.renderer) {
        this._defaultRender();
      } else {
        // TODO: currently lit-html default options
        const options = { scopeName: this.constructor.is, eventContext: this };
        this.constructor.renderer(this.render(), this.root, options);
        this._styleRender();
      }
      this._patchSlot(lightDom);
    }

    /**
     * patch slot incase of IE
     * @param {String} lightDOM - light dom inner HTML before render
     */
    _patchSlot(lightDOM) {
      const slot = this.root.querySelector('slot');

      if (!this.isCSS || !slot || !lightDOM) {
        return;
      }

      slot.outerHTML = lightDOM.replace('>', ' slot>');
    }

    /**
     * render string to HTML
     */
    _defaultRender() {
      const template = this._createTemplate(this.render());
      const instance = template.content.cloneNode(true);

      if (this.root.firstElementChild) {
        this.root.innerHTML = '';
      }

      if (this.root.innerText && this.root.innerText.trim() !== '') {
        this.root.innerText = '';
      }

      // append markup
      this.root.appendChild(instance);

      // render styles
      this._styleRender();

      // attach events to rendered dom
      if (this.bindEvents) {
        this.bindEvents();
      }

      this.renderComplete();
    }

    renderComplete() {}

    /**
     * render style tags inside the element
     */
    _styleRender() {
      // append styles tags
      (this.constructor.styles || []).map(style => {
        const styleTemplate = this._createTemplate(
          `<style>${style}</style>`,
          true
        );
        this.root.appendChild(styleTemplate.content.cloneNode(true));
      });

      if (!this.shadowRoot.adoptedStyleSheets.includes(globalStylesheet)) {
        this.shadowRoot.adoptedStyleSheets =
          [...this.shadowRoot.adoptedStyleSheets, globalStylesheet];
      }
    }

    /**
     * create HTML out of template tag
     * @param {String} innerHTML - content of template as string
     * @param {Boolean} isCSS - indicator of a css parsing
     * @return {TemplateElement} template to clone
     */
    _createTemplate(innerHTML, isCSS) {
      const template = document.createElement('template');
      // create template element from string
      template.innerHTML =
        this.isCSS && isCSS ? this._patchLightDomCSS(innerHTML) : innerHTML;

      // if no native shadow dom and polyfill included, use it
      if (window.ShadyCSS && !window.ShadyCSS.nativeRenderer && !this.isCSS) {
        window.ShadyCSS.prepareTemplate(template, this.nodeName.toLowerCase());
        window.ShadyCSS.styleElement(this);
      }
      return template;
    }

    /**
     * transform ShadyCSS to regular CSS to support older browsers
     * @param {String} css - a style content as string
     * @return {String} IE compatible css
     */
    _patchLightDomCSS(css) {
      const tag = this.constructor.is;
      return css
        .split('\n}\n')
        .filter(x => x.includes('{'))
        .map(cssLine =>
          cssLine
            .split('{')
            .map((expressions, index) =>
              expressions
                .split(',')
                .map(x =>
                  index !== 0
                    ? x
                    : x.includes('@')
                    ? x
                    : x.includes('::slotted(') && x.includes('::slotted(')
                    ? x
                      .replace(':host(', `${tag}`)
                      .replace('::slotted(', '')
                      .replace(')', ' ')
                      .replace(')', ' ')
                    : x.includes('::slotted(')
                    ? x.replace('::slotted(', ` ${tag} `).replace(')', ' ')
                    : x.includes(':host(')
                    ? x.replace(':host(', `${tag}`).replace(')', ' ')
                    : x.includes(':host')
                    ? // allow css-vars-ponyfill to work
                      x.replace(':host', `${tag}`)
                    : `${tag} ${x}`
                )
                .join(',')
            )
            .join('{')
        )
        .join('\n}\n');
    }
  };


